<template>
	<div :id="isSearchView ? 'content-header' : ''">
		<div v-if="isSearchView && personId" style="min-height: 125px">
			<ContentHeaderPersonDetails :personId="personId" :personBackdrop="personBackdrop" />
		</div>

		<div class="content-container container-fluid container-max-width" :class="{ 'center-banner': true }">
			<picture v-if="isMadeInEurope">
				<source type="image/webp" :srcset="`${assetsDir}/img/european.webp`" />
				<img
					height="43"
					width="200"
					style="width: 100%; max-width: 1000px; height: auto"
					:src="`${assetsDir}/img/european.png`"
				/>
			</picture>

			<div
				v-if="!isSearchView && !userStore.isLoggedIn"
				class="content-header"
				:class="{
					'content-header--expanded': expanded,
					'display-none': isMadeInEurope,
					'content-header--no-text': !h2Text && !h1Text,
				}"
				@click="toggleExpand()"
				data-testid="contentHeader-container"
			>
				<div
					v-if="lockedUrlMetadata?.htmlContentJson && !lockedUrlMetadata?.htmlContentJson.custom_content"
					v-html="
						isFilterContent
							? lockedUrlMetadata.htmlContentJson.filter_content
							: lockedUrlMetadata.htmlContentJson.custom_content
					"
				/>

				<div
					v-if="lockedUrlMetadata && !lockedUrlMetadata?.htmlContentJson?.custom_content && !isFilterContent"
					class="content-header__text-block"
				>
					<div>
						<h1 class="section-title" data-testid="contentHeader-title">{{ h1Title }}</h1>

						<div v-html="h1Text"></div>
						<h2 v-if="h2Title">{{ h2Title }}</h2>
						<div v-if="h2Text" v-html="h2Text"></div>
					</div>
				</div>

				<div data-testid="contentHeader-gradient" class="content-header--gradient" v-show="!expanded" />

				<div class="content-header__caret">
					<FontAwesomeIcon :icon="expanded ? faChevronUp : faChevronDown" />
				</div>
			</div>

			<div v-if="isSearchView && !personId" class="content-header__search-heading">
				<h1>
					{{ $t('WEBAPP_SEARCH_RESULTSFOR', { searchTerm: $route.query.q }) }}
				</h1>
			</div>

			<BasicButton
				v-if="showFilterButton"
				type="button"
				@click="toggleFilters"
				:filled="isFiltersVisible"
				:bordered="!isFiltersVisible"
				class="toggle-filters"
				color="tertiary"
				rounded
				data-testid="contentHeader-showFilter"
			>
				<span class="toggle-filters__label">
					{{ isFiltersVisible ? $t('WEBAPP_REMOVE_FILTERS') : $t('WEBAPP_FILTERS') }}
				</span>
				<FontAwesomeIcon :icon="faFilter" />
			</BasicButton>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { FilterName } from '@/stores/useFilterStoreTypes';

import { computed, ref } from 'vue';

import BasicButton from '@/ui-components/BasicButton.vue';
import { CollectionType } from '@/enums/collection-type';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { NewPageType } from '@/@types/graphql-types';

import { isAnyFilterActive } from '@/helpers/filter-helper';
import { faChevronUp, faChevronDown, faFilter } from '@fortawesome/free-solid-svg-icons';

import { useUserStore } from '@/stores/pinia/useUserStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

const ContentHeaderPersonDetails = defineAsyncComponent(
	() => import('@/components/content-header/ContentHeaderPersonDetails.vue')
);

const {
	lockedCollectionType,
	personId = null,
	personBackdrop = null,
	isSearchView = false,
	showFilterButton = false,
} = defineProps<{
	lockedCollectionType: CollectionType;
	personId?: string | null;
	personBackdrop?: string | null;
	isSearchView?: boolean;
	showFilterButton?: boolean;
}>();

const routingStore = useRoutingStore();
const filterStore = useFilterStore();
const userStore = useUserStore();

const assetsDir = `/${ASSETS_DIR}`;

const route = useRoute();
const isMadeInEurope = computed(() => route.name === 'app.titles.popular.european');

const isFilterContent = computed(() => {
	// pageType can be defined as pageType or newPageType on the route meta. PRICE_DROP is a newPageType
	const pageHasFilterContent =
		route.meta?.newPageType !== NewPageType.PriceDrop && route.meta?.pageType !== NewPageType.Upcoming;

	return pageHasFilterContent && (hasOtherFiltersActive() || filterStore.currentFilters.providers.length > 1);
});

// Lock urlMetadata to only update when we're on the correct collection type
// and the objectType is PAGE — prevents flashing next page's content during navigation
const lockedUrlMetadata = ref<typeof routingStore.urlMetadata>(null);

const h1Title = computed(() => lockedUrlMetadata.value?.heading1 ?? '');
const h1Text = computed(() => lockedUrlMetadata.value?.htmlContentJson?.h1_text ?? '');

const h2Title = computed(() => lockedUrlMetadata.value?.heading2 ?? '');
const h2Text = computed(() => lockedUrlMetadata.value?.htmlContentJson?.h2_text ?? '');

watch(
	() => routingStore.urlMetadata,
	newMeta => {
		if (
			newMeta?.objectType === 'PAGE' &&
			(lockedCollectionType === 'subgenre' ||
				routingStore.activeRoute?.meta?.collectionType === lockedCollectionType)
		) {
			lockedUrlMetadata.value = newMeta;
		}
	},
	{ immediate: true }
);

const expanded = ref(false);
function toggleExpand() {
	expanded.value = !expanded.value;
	TrackingHelper.trackEvent('content_header', { action: expanded.value ? 'expanded' : 'collapsed' });
}

const isFiltersVisible = ref(false);
function toggleFilters() {
	isFiltersVisible.value = !isFiltersVisible.value;
}

const isSubgenresFilterActive = computed(() => {
	return (
		(filterStore.currentFilters.subgenres || []).length > 0 &&
		!routingStore.activeRoute?.name?.toString().startsWith('app.titles.popular.subgenre')
	);
});

// checks if any filters are active that don't cause url changes and h1 text from urlMetadata to change.
const urlFilters: FilterName[] = [
	'providers',
	'content_types',
	'sort_asc',
	'sort_by',
	'sorting_random_seed',
	'subgenres',
];
function hasOtherFiltersActive() {
	const filters = (Object.keys(filterStore.currentFilters) as FilterName[]).filter(
		item => !urlFilters.includes(item)
	);

	return isAnyFilterActive(filters, filterStore.currentFilters) || isSubgenresFilterActive.value;
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';
@import '~@/styles/rtl-support-mixins';

.toggle-filters {
	display: flex;
	align-items: center;
	margin: 10px;

	&__label {
		margin-inline-end: 5px;
	}

	&.border.bg-none {
		color: $jw-color-lightgray;
	}
}

.content-container {
	display: flex;
	align-items: center;
	position: relative;
}

.center-banner {
	display: flex;
	justify-content: center;
}
.display-none {
	display: none;
}
.content-header {
	@include padding-two-row-viewport();
	min-height: 120px;
	max-height: 120px;
	overflow: hidden;
	position: relative;
	width: 100%;

	&:hover {
		cursor: pointer;
	}

	&--expanded {
		max-height: none;
	}

	&--no-text {
		min-height: 70px;
	}

	&--gradient {
		position: absolute;
		bottom: 0;
		@include inset-inline('start', 0);
		width: 100%;
		text-align: center;
		margin: 0;
		padding: 15px 0;
		background-image: linear-gradient(180deg, rgba($jw-color-darkblue--dark, 0), var(--jw-background-color));
	}

	h1 {
		padding-inline-end: 15px; //to make space for caret
	}

	&__search-heading {
		@include padding-two-row-viewport();
		flex-grow: 1;
		display: flex;
		align-items: center;
		& h1 {
			font-size: 24px;
			line-height: 32px;
			font-weight: 700;
			color: $jw-color-lightblue--light;

			@media (max-width: $screen-xs-max) {
				font-size: 16px;
				line-height: 24px;
			}
		}
		div {
			font-size: 15px;
			padding: 10px 0 5px;
		}
	}

	&__caret {
		position: absolute;
		@include inset-inline('end', 0);
		top: 18px;
		@media (max-width: $breakpoint-two-rows) {
			@include inset-inline('end', 20px);
		}
	}
}
</style>
